import React from 'react';
import { Skeleton } from './ui/skeleton';
import { Card, CardContent } from './ui/card';

/**
 * Dashboard Skeleton - Shows while dashboard is loading
 */
export function DashboardSkeleton() {
  return (
    <div className="space-y-4 p-4">
      {/* Header Skeleton */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-xl bg-slate-700" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-32 bg-slate-700" />
            <Skeleton className="h-3 w-24 bg-slate-800" />
          </div>
        </div>
        <Skeleton className="h-8 w-8 rounded-full bg-slate-700" />
      </div>

      {/* Trial Banner Skeleton */}
      <Skeleton className="h-12 w-full rounded-xl bg-slate-700/50" />

      {/* Main Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <Skeleton className="h-12 w-12 rounded-xl bg-slate-700" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-28 bg-slate-700" />
                  <Skeleton className="h-3 w-full bg-slate-800" />
                  <Skeleton className="h-3 w-3/4 bg-slate-800" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-3">
              <Skeleton className="h-4 w-16 mb-2 bg-slate-700" />
              <Skeleton className="h-6 w-12 bg-slate-700" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/**
 * Nutrition Tracker Skeleton - Shows while nutrition data is loading
 */
export function NutritionSkeleton() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950 border-none ios-header" style={{ border: 'none' }}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-9 rounded-full bg-slate-700" />
            <Skeleton className="h-9 w-9 rounded-xl bg-slate-700" />
            <Skeleton className="h-5 w-24 bg-slate-700" />
          </div>
          <Skeleton className="h-8 w-16 rounded bg-slate-700" />
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="max-w-lg mx-auto space-y-4">
          {/* Date Navigation */}
          <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <Skeleton className="h-8 w-8 rounded bg-slate-700" />
                <div className="text-center space-y-1">
                  <Skeleton className="h-5 w-24 mx-auto bg-slate-700" />
                  <Skeleton className="h-3 w-20 mx-auto bg-slate-800" />
                </div>
                <Skeleton className="h-8 w-8 rounded bg-slate-700" />
              </div>
            </CardContent>
          </Card>

          {/* Calories Card */}
          <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-xl bg-slate-700" />
                  <Skeleton className="h-5 w-28 bg-slate-700" />
                </div>
                <Skeleton className="h-7 w-20 bg-slate-700" />
              </div>
              <Skeleton className="h-3 w-full rounded-full bg-slate-800" />
              <Skeleton className="h-3 w-24 mt-2 ml-auto bg-slate-800" />
            </CardContent>
          </Card>

          {/* Macros Grid */}
          <div className="grid grid-cols-3 gap-2">
            {['Protein', 'Carbs', 'Fat'].map((macro) => (
              <Card key={macro} className="bg-slate-900/80 border-slate-800 rounded-xl">
                <CardContent className="p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Skeleton className="h-6 w-6 rounded-lg bg-slate-700" />
                    <Skeleton className="h-3 w-12 bg-slate-800" />
                  </div>
                  <Skeleton className="h-5 w-16 bg-slate-700" />
                  <Skeleton className="h-1.5 w-full mt-1 rounded-full bg-slate-800" />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Action Buttons Grid */}
          <div className="grid grid-cols-4 gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Skeleton key={i} className="h-16 rounded-xl bg-slate-800/50" />
            ))}
          </div>

          {/* Meal Times */}
          <div className="space-y-3">
            {['Breakfast', 'Lunch', 'Dinner'].map((meal) => (
              <Card key={meal} className="bg-slate-900/80 border-slate-800 rounded-xl">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-5 w-5 rounded bg-slate-700" />
                      <Skeleton className="h-4 w-20 bg-slate-700" />
                    </div>
                    <Skeleton className="h-4 w-12 bg-slate-800" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * Statistics Skeleton - Shows while stats are loading
 */
export function StatisticsSkeleton() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950 border-none ios-header" style={{ border: 'none' }}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-9 rounded-full bg-slate-700" />
            <Skeleton className="h-9 w-9 rounded-xl bg-slate-700" />
            <Skeleton className="h-5 w-24 bg-slate-700" />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Period Tabs */}
          <div className="flex gap-2">
            {['Week', 'Month', 'Year'].map((period) => (
              <Skeleton key={period} className="h-9 w-20 rounded-lg bg-slate-800" />
            ))}
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="bg-slate-900/80 border-slate-800 rounded-xl">
                <CardContent className="p-4">
                  <Skeleton className="h-3 w-16 mb-2 bg-slate-800" />
                  <Skeleton className="h-7 w-20 bg-slate-700" />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Chart Area */}
          <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
            <CardContent className="p-4">
              <Skeleton className="h-5 w-32 mb-4 bg-slate-700" />
              <Skeleton className="h-64 w-full rounded-lg bg-slate-800/50" />
            </CardContent>
          </Card>

          {/* History List */}
          <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
            <CardContent className="p-4 space-y-3">
              <Skeleton className="h-5 w-28 bg-slate-700" />
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-800">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-8 w-8 rounded bg-slate-700" />
                    <div className="space-y-1">
                      <Skeleton className="h-4 w-24 bg-slate-700" />
                      <Skeleton className="h-3 w-16 bg-slate-800" />
                    </div>
                  </div>
                  <Skeleton className="h-5 w-12 bg-slate-700" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

/**
 * Food Entry Skeleton - Shows while food items are loading
 */
export function FoodEntrySkeleton({ count = 3 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="p-3 bg-slate-800/30 rounded-xl border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Skeleton className="h-4 w-32 mb-1 bg-slate-700" />
              <div className="flex gap-3 mt-1">
                <Skeleton className="h-3 w-14 bg-slate-800" />
                <Skeleton className="h-3 w-10 bg-slate-800" />
                <Skeleton className="h-3 w-10 bg-slate-800" />
                <Skeleton className="h-3 w-10 bg-slate-800" />
              </div>
            </div>
            <div className="flex gap-1">
              <Skeleton className="h-7 w-7 rounded bg-slate-700" />
              <Skeleton className="h-7 w-7 rounded bg-slate-700" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Card Skeleton - Generic loading card
 */
export function CardSkeleton({ className = '' }) {
  return (
    <Card className={`bg-slate-900/80 border-slate-800 rounded-2xl ${className}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Skeleton className="h-12 w-12 rounded-xl bg-slate-700" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-28 bg-slate-700" />
            <Skeleton className="h-3 w-full bg-slate-800" />
            <Skeleton className="h-3 w-3/4 bg-slate-800" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * List Item Skeleton - Generic list item loading state
 */
export function ListItemSkeleton({ count = 5 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg">
          <Skeleton className="h-10 w-10 rounded-lg bg-slate-700" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-4 w-32 bg-slate-700" />
            <Skeleton className="h-3 w-24 bg-slate-800" />
          </div>
          <Skeleton className="h-8 w-16 rounded bg-slate-700" />
        </div>
      ))}
    </div>
  );
}

/**
 * Gym Search Skeleton - Shows while searching for gyms
 */
export function GymSearchSkeleton({ count = 3 }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-3">
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-40 bg-slate-700" />
                <Skeleton className="h-3 w-56 bg-slate-800" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-3 w-3 rounded-full bg-yellow-500/30" />
                  <Skeleton className="h-3 w-16 bg-slate-800" />
                </div>
              </div>
              <Skeleton className="h-8 w-8 rounded bg-slate-700" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * AI Planner Skeleton - Shows while AI is generating or loading plans
 */
export function AiPlannerSkeleton() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950 border-none ios-header" style={{ border: 'none' }}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-9 rounded-full bg-slate-700" />
            <Skeleton className="h-9 w-9 rounded-xl bg-slate-700" />
            <Skeleton className="h-5 w-28 bg-slate-700" />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="max-w-lg mx-auto space-y-4">
          {/* Plan Type Tabs */}
          <div className="flex gap-2">
            <Skeleton className="h-10 flex-1 rounded-lg bg-slate-800" />
            <Skeleton className="h-10 flex-1 rounded-lg bg-slate-800" />
          </div>

          {/* Form Fields */}
          <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-24 bg-slate-700" />
                <Skeleton className="h-10 w-full rounded-lg bg-slate-800" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-32 bg-slate-700" />
                <Skeleton className="h-10 w-full rounded-lg bg-slate-800" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-28 bg-slate-700" />
                <Skeleton className="h-20 w-full rounded-lg bg-slate-800" />
              </div>
            </CardContent>
          </Card>

          {/* Generate Button */}
          <Skeleton className="h-12 w-full rounded-xl bg-gradient-to-r from-violet-900/50 to-purple-900/50" />

          {/* Saved Plans Section */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-28 bg-slate-700" />
            {[1, 2].map((i) => (
              <Card key={i} className="bg-slate-900/80 border-slate-800 rounded-xl">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32 bg-slate-700" />
                      <Skeleton className="h-3 w-24 bg-slate-800" />
                    </div>
                    <Skeleton className="h-8 w-8 rounded bg-slate-700" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * AI Plan Generation Loading - Shows animated skeleton while AI is thinking
 */
export function AiGeneratingPlanSkeleton() {
  return (
    <Card className="bg-slate-900/80 border-slate-800 rounded-2xl overflow-hidden">
      <CardContent className="p-6">
        <div className="flex flex-col items-center justify-center space-y-4">
          {/* Animated brain icon placeholder */}
          <div className="relative">
            <Skeleton className="h-16 w-16 rounded-full bg-gradient-to-br from-violet-600/30 to-purple-600/30" />
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-500/20 to-purple-500/20 animate-ping" />
          </div>
          
          {/* Text placeholders */}
          <div className="text-center space-y-2">
            <Skeleton className="h-5 w-48 mx-auto bg-slate-700" />
            <Skeleton className="h-3 w-64 mx-auto bg-slate-800" />
          </div>
          
          {/* Progress dots */}
          <div className="flex gap-2">
            {[1, 2, 3].map((i) => (
              <div 
                key={i} 
                className="h-2 w-2 rounded-full bg-violet-500 animate-pulse"
                style={{ animationDelay: `${i * 200}ms` }}
              />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * AI Plan Result Skeleton - Shows while plan content is rendering
 */
export function AiPlanResultSkeleton() {
  return (
    <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
      <CardContent className="p-4 space-y-4">
        {/* Plan Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-xl bg-slate-700" />
            <div className="space-y-1">
              <Skeleton className="h-5 w-32 bg-slate-700" />
              <Skeleton className="h-3 w-24 bg-slate-800" />
            </div>
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-8 w-8 rounded bg-slate-700" />
            <Skeleton className="h-8 w-8 rounded bg-slate-700" />
          </div>
        </div>
        
        {/* Plan Content Sections */}
        <div className="space-y-3">
          {[1, 2, 3].map((section) => (
            <div key={section} className="space-y-2 p-3 bg-slate-800/30 rounded-lg">
              <Skeleton className="h-4 w-28 bg-slate-700" />
              <div className="space-y-1">
                <Skeleton className="h-3 w-full bg-slate-800" />
                <Skeleton className="h-3 w-5/6 bg-slate-800" />
                <Skeleton className="h-3 w-4/6 bg-slate-800" />
              </div>
            </div>
          ))}
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Skeleton className="h-10 flex-1 rounded-lg bg-slate-700" />
          <Skeleton className="h-10 flex-1 rounded-lg bg-slate-700" />
        </div>
      </CardContent>
    </Card>
  );
}

export default {
  DashboardSkeleton,
  NutritionSkeleton,
  StatisticsSkeleton,
  FoodEntrySkeleton,
  CardSkeleton,
  ListItemSkeleton,
  GymSearchSkeleton,
  AiPlannerSkeleton,
  AiGeneratingPlanSkeleton,
  AiPlanResultSkeleton,
};
